import React from 'react';

import { useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import Button from '@atlaskit/button/default/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { HelpPageAri } from '@atlassian/ari/help';

import messages from '../messages';

// Local stand-ins for the upcoming Help Center site-tree GraphQL types.
// Replace with Relay-generated types once the schema is available.
export type HelpCenterPage = {
	__typename: 'HelpCenterPage';
	id: string;
	name: {
		default: string;
	};
};

export type HelpCenterFolder = {
	__typename: 'HelpCenterFolder';
	contents: {
		edges: Array<{
			node: HelpCenterPage;
		}>;
	};
	id: string;
	name: {
		default: string;
	};
};

export type HelpCenterSiteTreeNode = HelpCenterFolder | HelpCenterPage;

const getFolderPages = (folder: HelpCenterFolder): HelpCenterPage[] =>
	folder.contents.edges.map((edge) => edge.node);

type PageItemProps = {
	page: HelpCenterPage;
};

const PageItem = ({ page }: PageItemProps) => {
	const { push } = useRouterActions();
	const pageId = HelpPageAri.parse(page.id).pageId;

	return <DropdownItem onClick={() => push(`/page/${pageId}`)}>{page.name.default}</DropdownItem>;
};

const FolderMenuItems = ({ pages }: { pages: HelpCenterPage[] }) => (
	<DropdownItemGroup>
		{pages.map((page) => (
			<PageItem key={page.id} page={page} />
		))}
	</DropdownItemGroup>
);

type FolderMenuProps = {
	folder: HelpCenterFolder;
};

const PromotedFolderMenu = ({ folder }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="bottom-start"
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					appearance="subtle"
					iconAfter={(iconProps) => (
						<ChevronDownIcon {...iconProps} label={formatMessage(messages.downIcon)} />
					)}
					ref={triggerRef}
				>
					{folder.name.default}
				</Button>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const OverflowFolderMenu = ({ folder }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="right-start"
			shouldFlip
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<DropdownItem
					{...props}
					elemAfter={
						<ChevronRightIcon label={formatMessage(messages.openFolderSubmenu)} size="small" />
					}
					ref={triggerRef}
				>
					{folder.name.default}
				</DropdownItem>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const OverflowItem = ({ item }: { item: HelpCenterSiteTreeNode }) =>
	item.__typename === 'HelpCenterPage' ? (
		<PageItem page={item} />
	) : (
		<OverflowFolderMenu folder={item} />
	);

export const SiteNavigationMenus = ({
	overflowItems,
	promotedFolders,
	// Kept for API parity with MoreDropdown. `@atlaskit/button/default/button` omits `style`, so dynamic
	// navbar textColor theming is deferred until a supported approach is available.
	textColor: _textColor,
}: {
	overflowItems: HelpCenterSiteTreeNode[];
	promotedFolders: HelpCenterFolder[];
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (promotedFolders.length === 0 && overflowItems.length === 0) {
		return null;
	}

	return (
		<>
			{promotedFolders.map((folder) => (
				<PromotedFolderMenu key={folder.id} folder={folder} />
			))}
			{overflowItems.length > 0 && (
				<DropdownMenu
					shouldRenderToParent
					trigger={({ triggerRef, ...props }) => (
						<Button
							{...props}
							appearance="subtle"
							testId="site-navigation-more"
							iconAfter={(iconProps) => (
								<ChevronDownIcon {...iconProps} label={formatMessage(messages.downIcon)} />
							)}
							ref={triggerRef}
						>
							{formatMessage(messages.more)}
						</Button>
					)}
				>
					<DropdownItemGroup>
						{overflowItems.map((item) => (
							<OverflowItem key={item.id} item={item} />
						))}
					</DropdownItemGroup>
				</DropdownMenu>
			)}
		</>
	);
};

export const SiteNavigation = ({
	queryRef: _queryRef,
	textColor,
}: {
	// TODO: Replace `null` with `siteNavigation_AtlassianHubAppShell$key` once siteTree is available.
	queryRef: null;
	textColor?: string;
}): React.JSX.Element | null => {
	// Folders shown as their own dropdown buttons in the navbar (before More).
	const promotedFolders: HelpCenterFolder[] = [];
	// Pages/folders that don't fit inline and are collected under the More menu.
	const overflowItems: HelpCenterSiteTreeNode[] = [];

	return (
		<SiteNavigationMenus
			promotedFolders={promotedFolders}
			overflowItems={overflowItems}
			textColor={textColor}
		/>
	);
};
